import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { DxHtmlEditorComponent } from 'devextreme-angular/ui/html-editor';
import { lastValueFrom } from 'rxjs';
import { AjaxService } from 'AjaxService';
import { IBemerkungenViewModel } from './bemerkungen.component';
import { BEMERKUNG_VORLAGE, BILD_LIMITS, BemerkungsBild, bereinigterInhalt, bildReferenzen, fehlermeldung, pruefeBildmenge, textAlsHtml } from './bemerkungen-html.model';

@Component({
  selector: 'cz-bemerkungen-html-editor', standalone: false,
  templateUrl: './bemerkungen-html-editor.component.html', styleUrls: ['./bemerkungen-html.component.css']
})
export class BemerkungenHtmlEditorComponent implements OnDestroy {
  @Input() bemerkung: IBemerkungenViewModel;
  @Output() gespeichert = new EventEmitter<void>();
  @Output() abgebrochen = new EventEmitter<void>();
  @ViewChild('editor') editor: DxHtmlEditorComponent;
  @ViewChild('dateiauswahl') dateiauswahl: ElementRef<HTMLInputElement>;
  betreff = '';
  html = '';
  laden = true;
  bilderWerdenGeladen = false;
  speichern = false;
  bilderPruefen = false;
  fehler = '';
  hinweis = '';
  konflikt = false;
  ladefehler = false;
  private gestartet = false;
  private beendet = false;
  private root: HTMLElement;
  private urls = new Map<string, string>();
  private uploads = new Map<string, File>();
  private metadata = new Map<string, BemerkungsBild>();
  private position = 0;
  readonly toolbar = {
    multiline: true,
    items: ['undo', 'redo', 'separator', 'bold', 'italic', 'underline', 'separator', 'orderedList', 'bulletList', 'clear',
      { widget: 'dxButton', options: { icon: 'image', text: 'Bild einfügen', onClick: () => this.dateiWaehlen() } }]
  };

  constructor(private ajax: AjaxService) {}

  initialisieren(e) {
    const Image = e.component.get('formats/extendedImage');
    class BemerkungsBildFormat extends Image {
      static create(value) {
        const node = super.create({});
        node.removeAttribute('src');
        if (value && typeof value === 'object') {
          if (value.bildId) node.setAttribute('data-bild-id', value.bildId);
          if (value.src?.startsWith('blob:')) node.setAttribute('src', value.src);
          for (const key of ['alt', 'width', 'height']) if (value[key]) node.setAttribute(key, value[key]);
        }
        return node;
      }
      static value(node) { return { ...super.value(node), bildId: node.getAttribute('data-bild-id') }; }
    }
    e.component.register({ 'formats/extendedImage': BemerkungsBildFormat });
  }

  moduleAnpassen = modules => {
    modules.dropImage = false;
    modules.uploader = { mimetypes: [], handler: () => {} };
  };

  bereit() {
    if (this.gestartet) return;
    this.gestartet = true;
    const quill = this.editor.instance.getQuillInstance();
    this.root = quill.root;
    this.root.setAttribute('aria-label', 'Bemerkungstext');
    this.root.addEventListener('paste', this.einfuegen, true);
    this.root.addEventListener('drop', this.dropVerhindern, true);
    this.root.addEventListener('dragover', this.dropVerhindern, true);
    // ContentReady läuft noch während Angular die übergeordnete Ansicht prüft.
    queueMicrotask(() => {
      if (this.beendet) return;
      this.betreff = this.bemerkung.betreff || '';
      void this.bilderLaden();
    });
  }

  async bilderLaden() {
    this.laden = true;
    this.ladefehler = false;
    this.fehler = '';
    try {
      for (const bild of this.bemerkung.bilder || []) {
        this.metadata.set(bild.bildId, bild);
        if (this.urls.has(bild.bildId)) continue;
        this.bilderWerdenGeladen = true;
        const blob = await this.ajax.downloadResource(`api/Bemerkungen/${this.bemerkung.id}/Bilder/${bild.bildId}`);
        if (this.beendet) return;
        this.urls.set(bild.bildId, URL.createObjectURL(blob));
      }
      const content = this.bemerkung.id ? (this.bemerkung.textHtml ?? textAlsHtml(this.bemerkung.text)) : BEMERKUNG_VORLAGE;
      this.html = bereinigterInhalt(content, this.urls).innerHTML;
      this.editor.instance.option('value', this.html);
      if (!this.bemerkung.id) {
        // Quill entfernt beim HTML-Import den letzten leeren Absatz der Vorlage.
        const quill = this.editor.instance.getQuillInstance();
        quill.insertText(quill.getLength() - 1, '\n', 'api');
      }
    } catch (error) {
      this.ladefehler = true;
      this.fehler = 'Die Bilder konnten nicht geladen werden. Bitte versuchen Sie es erneut.';
    } finally {
      this.bilderWerdenGeladen = false;
      this.laden = false;
    }
  }

  dateiWaehlen() {
    if (this.laden || this.speichern || this.bilderPruefen || this.ladefehler) return;
    const selection = this.editor.instance.getSelection();
    this.position = selection?.index ?? Math.max(0, this.editor.instance.getQuillInstance().getLength() - 1);
    this.dateiauswahl.nativeElement.click();
  }

  async dateienAusgewaehlt(event: Event) {
    const input = event.target as HTMLInputElement;
    await this.bilderEinfuegen(Array.from(input.files || []), this.position);
    input.value = '';
  }

  private einfuegen = (event: ClipboardEvent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.laden || this.speichern || this.bilderPruefen || this.ladefehler) return;
    const quill = this.editor.instance.getQuillInstance();
    const range = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) { void this.bilderEinfuegen(files, range.index); return; }
    const pasted = event.clipboardData?.getData('text/html');
    if (pasted) {
      const content = bereinigterInhalt(pasted, this.urls);
      let entfernt = /<img\b/i.test(pasted) && !content.querySelector('img');
      content.querySelectorAll('img').forEach(img => {
        if (!this.urls.has(img.getAttribute('data-bild-id'))) { img.remove(); entfernt = true; }
      });
      if (entfernt) this.hinweis = 'Bilder aus fremden Inhalten wurden nicht übernommen. Bitte verwenden Sie die Dateiauswahl oder kopieren Sie das Bild selbst.';
      const delta = quill.clipboard.convert({ html: content.innerHTML, text: '' });
      const Delta = this.editor.instance.get('delta');
      quill.updateContents(new Delta().retain(range.index).delete(range.length).concat(delta), 'user');
      quill.setSelection(range.index + delta.length(), 0, 'silent');
    } else {
      quill.deleteText(range.index, range.length, 'user');
      quill.insertText(range.index, event.clipboardData?.getData('text/plain') || '', 'user');
    }
  };

  private dropVerhindern = (event: DragEvent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.type === 'drop') this.hinweis = 'Bitte fügen Sie Bilder über „Bild einfügen“ oder Strg+V ein.';
  };

  private async bilderEinfuegen(files: File[], index: number) {
    if (!files.length || this.bilderPruefen) return;
    this.bilderPruefen = true;
    this.fehler = '';
    const vorbereitet: { id: string; file: File; url: string; breite: number; hoehe: number }[] = [];
    try {
      const refs = bildReferenzen(this.editor.instance.option('value') as string);
      const groessen = [...new Set(refs)].map(id => this.uploads.get(id)?.size ?? this.metadata.get(id)?.dateigroesse ?? 0);
      pruefeBildmenge([...groessen, ...files.map(file => file.size)], refs.length + files.length);
      for (const file of files) {
        if (!['image/png', 'image/jpeg'].includes(file.type)) throw new Error('Bitte wählen Sie PNG- oder JPEG-Bilder aus.');
        const url = URL.createObjectURL(file);
        const bild = { id: crypto.randomUUID(), file, url, breite: 0, hoehe: 0 };
        vorbereitet.push(bild);
        const image = new Image();
        image.src = url;
        try { await image.decode(); } catch { throw new Error('Das Bild ist beschädigt oder kann nicht gelesen werden.'); }
        if (image.naturalWidth * image.naturalHeight > BILD_LIMITS.pixel) throw new Error('Ein Bild darf höchstens 40 Megapixel umfassen.');
        bild.breite = image.naturalWidth;
        bild.hoehe = image.naturalHeight;
      }
      if (this.beendet) return;
      for (const bild of vorbereitet) {
        this.uploads.set(bild.id, bild.file);
        this.urls.set(bild.id, bild.url);
        const width = Math.min(bild.breite, 600);
        this.editor.instance.insertEmbed(index++, 'extendedImage', { bildId: bild.id, src: bild.url, alt: bild.file.name || 'Eingefügtes Bild', width, height: Math.round(bild.hoehe * width / bild.breite) });
      }
      this.editor.instance.setSelection(index, 0);
    } catch (error) { this.fehler = fehlermeldung(error); }
    finally {
      for (const bild of vorbereitet) if (!this.urls.has(bild.id)) URL.revokeObjectURL(bild.url);
      this.bilderPruefen = false;
    }
  }

  get neueBilder(): number {
    return [...new Set(bildReferenzen(this.html))].filter(id => this.uploads.has(id)).length;
  }

  async speichernKlick() {
    if (this.speichern || this.laden || this.ladefehler || this.bilderPruefen) return;
    this.fehler = '';
    this.konflikt = false;
    if (!this.betreff.trim()) { this.fehler = 'Bitte geben Sie einen Betreff ein.'; return; }
    this.speichern = true;
    try {
      const textHtml = bereinigterInhalt(this.editor.instance.option('value') as string).innerHTML;
      const refs = bildReferenzen(textHtml);
      const ids = [...new Set(refs)];
      pruefeBildmenge(ids.map(id => this.uploads.get(id)?.size ?? this.metadata.get(id)?.dateigroesse ?? 0), refs.length);
      const form = new FormData();
      form.append('context', JSON.stringify({ id: this.bemerkung.id || 0, ownerId: this.bemerkung.ownerId, entityArt: this.bemerkung.entityArt,
        betreff: this.betreff.trim(), textHtml, rowVersion: this.bemerkung.rowVersion, owi104: this.bemerkung.owi104 }));
      for (const id of ids) {
        const file = this.uploads.get(id);
        if (file) form.append(id, file, file.name || 'Zwischenablage.png');
      }
      await lastValueFrom(this.ajax.post('api/Bemerkungen/SaveHtml', form, { contentType: false }));
      this.gespeichert.emit();
    } catch (error) {
      this.fehler = fehlermeldung(error);
      this.konflikt = (error as any)?.status === 409;
    } finally { this.speichern = false; }
  }

  neuLaden() {
    if (window.confirm('Den aktuellen Stand laden und den eigenen Entwurf verwerfen?')) this.gespeichert.emit();
  }

  ngOnDestroy() {
    this.beendet = true;
    this.root?.removeEventListener('paste', this.einfuegen, true);
    this.root?.removeEventListener('drop', this.dropVerhindern, true);
    this.root?.removeEventListener('dragover', this.dropVerhindern, true);
    this.urls.forEach(url => URL.revokeObjectURL(url));
    this.urls.clear();
  }
}
